/**
 * Descuento web SOLO presentacional.
 *
 * Un único porcentaje global controla la UI de precio tachado / rebajado en las
 * superficies WEB (landing pricing, package selector, checkout modal). NO calcula
 * el cobro real: el cargo lo aplica DodoPayments mediante el campo
 * "Discount Applicable (%)" de cada producto, que DEBE fijarse al mismo valor.
 *
 * Se lee de forma LAZY (dentro de las funciones, nunca como const de módulo) para
 * que: (a) Next inline `NEXT_PUBLIC_*` correctamente en build, y (b) los tests de
 * vitest puedan mutar `process.env` por test sin `vi.resetModules()`.
 */

/**
 * Porcentaje de descuento web activo, acotado a [0, 100].
 * Vacío / inválido / NaN / <= 0 -> 0 (feature apagada, UI sin cambios).
 */
export function getWebDiscountPercent(): number {
  const raw = process.env.NEXT_PUBLIC_WEB_DISCOUNT_PERCENT
  if (!raw) return 0

  const parsed = Number.parseFloat(raw.trim())
  if (!Number.isFinite(parsed) || parsed <= 0) return 0

  return Math.min(parsed, 100)
}

/** True cuando hay un descuento activo (> 0). */
export function isWebDiscountActive(): boolean {
  return getWebDiscountPercent() > 0
}

/**
 * Precio con descuento, redondeado a 2 decimales (half-up sobre céntimos).
 *
 * El redondeo es explícito y determinista. OJO: Dodo calcula su propio cargo a
 * partir del precio del producto menos el "Discount Applicable (%)" y puede
 * redondear distinto en la frontera de medio céntimo. El número mostrado debe
 * verificarse contra el preview de checkout de Dodo (ver DESIGN.md).
 */
export function getDiscountedPrice(price: number): number {
  const pct = getWebDiscountPercent()
  if (pct <= 0) return price
  return Math.round(price * (1 - pct / 100) * 100) / 100
}

/** Etiqueta entera para el badge, p.ej. 20 -> "-20%". */
export function getWebDiscountLabel(): string {
  return `-${Math.round(getWebDiscountPercent())}%`
}
